import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { generateColorScheme, generateDefaultColorSchemes } from 'owa-color-utils/lib/colorUtils';
import CalendarColor from 'owa-service/lib/contract/CalendarColor';
import { findInArray } from './findInArray';

let defaultColorSchemes: ColorScheme[];

/**
 * The order of colors in CalendarColor enum to assign by default. If 0 is taken, assign 5, etc.
 */
const colorIndexOrder: number[] = [0, 5, 1, 3, 8, 6, 7, 2, 4];

/**
 * The map of the number of times each default color has been used. Initialized to all 0 values.
 */
let usedDefaultColors: number[] = colorIndexOrder.map(element => 0);

/**
 * The default color schemes
 */
export function getDefaultColorSchemes(): ColorScheme[] {
    if (!defaultColorSchemes) {
        initializeDefaultColorSchemes();
    }

    return defaultColorSchemes;
}

/**
 * The default color schemes ordered by the colorIndexOrder
 */
export function getDefaultColorSchemesOrderedByColorIndex(): ColorScheme[] {
    return colorIndexOrder.map<ColorScheme>(colorIndex => {
        return getDefaultColorSchemes()[colorIndex];
    });
}

/**
 * Initialize the default color schemes based on current theme, reset usage of automatic colors
 */
export function initializeDefaultColorSchemes() {
    defaultColorSchemes = generateDefaultColorSchemes();
    usedDefaultColors = colorIndexOrder.map(element => 0);
}

/**
 * Gets the color scheme for the given CalendarColor. If CalendarColor is auto, gets the next unused ColorScheme
 * from the available default schemes.
 * @param calendarColor the CalendarColor to generate a scheme for
 * @param colorsUsed the previously used colors, for automatic color picking. If undefined, will default to the general cached colors
 */
export function getColorSchemeForCalendarColor(calendarColor: CalendarColor): ColorScheme {
    let usedIndex: number = calendarColor;
    if (calendarColor === undefined || calendarColor === CalendarColor.Auto) {
        usedIndex = calculateNextAutoColorSchemeIndex();
        // mark that color as having been used
        usedDefaultColors[usedIndex]++;
    } else {
        usedIndex = colorIndexOrder.indexOf(calendarColor);
    }

    return getDefaultColorSchemes()[colorIndexOrder[usedIndex]];
}

/**
 * Generates the color scheme for the given primary hex color value
 * @param color the primary color hex value
 */
export function getColorSchemeForHexColor(color: string): ColorScheme {
    return generateColorScheme(color);
}

/**
 * Releases a calendar color from use, freeing it up for future CalendarColor.Auto usage
 * @param calendarColor the color no longer being used
 */
export function releaseCalendarColor(calendarColor: CalendarColor | string): void {
    let index;
    if (typeof calendarColor === 'string') {
        index = getCalendarColorFromCalPrimaryString(calendarColor);
    } else {
        index = calendarColor;
    }

    let mappedIndex = colorIndexOrder.indexOf(index);

    usedDefaultColors[mappedIndex] = Math.max(usedDefaultColors[mappedIndex] - 1, 0);
}

/**
 * Gets the CalendarColor with the given calPrimary value. Returns Automatic if none match
 * @param calPrimary the string for the calPrimary color
 */
function getCalendarColorFromCalPrimaryString(calPrimary: string): CalendarColor {
    let defaultColorEntry = findInArray(getDefaultColorSchemes(), item => {
        return item.calPrimary == calPrimary;
    });

    return defaultColorEntry.index;
}

/**
 * Looks through the used default colors to see which is the least used, then
 * returns the defaultColorScheme index (retrieved from colorIndexOrder) of the color scheme
 */
function calculateNextAutoColorSchemeIndex(): number {
    const minimumValue = Math.min(...usedDefaultColors);
    const indexOfMin = usedDefaultColors.indexOf(minimumValue);
    return indexOfMin;
}
